import { useEffect, useRef } from 'react';
import { JEWEL_FAMILIES, JEWEL_FAMILY_BEHAVIOURS } from './jewel-appearance';
import { registerJewelFilters } from './jewel-motion';

/**
 * Os filtros elétricos das joias - UM conjunto para as quinze (gate V28C.2).
 *
 * ## Crédito e licença
 *
 * A linguagem visual - ruído a percorrer um contorno, com brilho por baixo - é
 * a do componente **Electric Border** da coleção **React Bits**
 * (https://reactbits.dev), de **@BalintFerenczy**.
 *
 * A licença do repositório oficial é **MIT + Commons Clause**, e não MIT
 * simples: a cláusula acrescenta uma restrição à venda do próprio software.
 * Este comentário existe para preservar o aviso e o crédito - e para não
 * afirmar uma licença que o projeto não tem. Uma correção anterior desta mesma
 * folha chamava-lhe MIT, o que era errado.
 *
 * ## A diferença de implementação, dita com exatidão
 *
 * O código de referência desenha num **`<canvas>`**, com um laço de
 * `requestAnimationFrame` e um `ResizeObserver` POR INSTÂNCIA. Esta adaptação
 * não usa canvas nenhum: usa um **filtro SVG partilhado** - `feTurbulence`
 * deslocada por `feOffset` animado em SMIL e aplicada com `feDisplacementMap`
 * a uma borda CSS. Quinze cartas referem nove filtros; o browser anima-os
 * sozinho.
 *
 * A escolha não é estética: quinze canvas seriam quinze contextos de desenho, e
 * este projeto tem uma regra sobre isso - um `Phaser.Game` por aba, nada de
 * laços por elemento. Zero canvas, zero WebGL, zero RAF, zero `ResizeObserver`.
 *
 * ## Como se suspende
 *
 * SMIL não obedece a `animation-play-state` nem a `prefers-reduced-motion`: só
 * `svg.pauseAnimations()` o susta. Por isso este componente regista-se no
 * controlador partilhado, que o suspende quando a aba fica escondida ou quando
 * o jogador pede menos movimento.
 */

/**
 * O que cada família pede ao ruído.
 *
 * A amplitude é medida contra o RAIO do aro, e não contra um cartão de página
 * inteira: um aro de 110px deslocado por 11 dobra-se sobre si próprio e deixa
 * de se ler como linha - vira uma nuvem com mordidas escuras. Cinco pixels
 * ondulam a linha e abrem pequenas quebras, que é o que se quer.
 *
 * `frequency` é o tamanho do grão - grande faz filamentos nervosos, pequeno faz
 * ondas largas. `scale` é o quanto o contorno é deslocado, em pixels. `octaves`
 * é a riqueza do detalhe. `seconds` é o tempo que o ruído leva a percorrer o
 * ciclo: é ele que faz uma família parecer rápida ou serena.
 */
const NOISE: Readonly<
  Record<string, { frequency: number; octaves: number; scale: number; seconds: number }>
> = Object.freeze({
  electric: { frequency: 0.058, octaves: 4, scale: 8, seconds: 2.2 },
  plasma: { frequency: 0.05, octaves: 4, scale: 6.5, seconds: 4.6 },
  ember: { frequency: 0.054, octaves: 4, scale: 7.5, seconds: 3.1 },
  blood: { frequency: 0.046, octaves: 3, scale: 6, seconds: 5.8 },
  shield: { frequency: 0.048, octaves: 3, scale: 5.5, seconds: 4.3 },
  toxic: { frequency: 0.052, octaves: 4, scale: 7, seconds: 7.1 },
  vital: { frequency: 0.044, octaves: 3, scale: 5.5, seconds: 6.3 },
  gale: { frequency: 0.052, octaves: 4, scale: 6.5, seconds: 2.7 },
  /* A soberana desloca pouco: o filamento tem de continuar colado à forma. */
  sovereign: { frequency: 0.058, octaves: 3, scale: 7, seconds: 3.4 },
});

/**
 * O id do filtro de uma família.
 *
 * São dois por família: o do FILAMENTO, que percorre o contorno inteiro, e o
 * dos RAMOS, com grão mais fino e deslocamento maior - sem ele, os ramos saíam
 * como arcos longos e lisos, que se lêem como um traço solto ao lado da joia em
 * vez de eletricidade a saltar.
 */
export function jewelFilterId(family: string, kind: 'arc' | 'branch' = 'arc'): string {
  return kind === 'branch' ? `jewel-arc-${family}-branch` : `jewel-arc-${family}`;
}

/**
 * Um filtro: quatro ruídos que escorrem em direções opostas, e um deslocamento.
 *
 * Os pares vertical e horizontal misturam-se para o filamento não ter uma
 * direção óbvia - é isso que o faz parecer eletricidade em vez de uma textura a
 * deslizar. A estrutura é a do Electric Border, com os números afinados para
 * uma carta de inventário em vez de um cartão de página inteira.
 */
function ElectricFilter({ family, kind }: { family: string; kind: 'arc' | 'branch' }) {
  const noise = NOISE[family] ?? NOISE.electric;
  if (!noise) {
    return null;
  }
  const branch = kind === 'branch';
  const frequency = branch ? noise.frequency * 1.4 : noise.frequency;
  const octaves = noise.octaves;
  /*
   * Os ramos deslocam MUITO mais do que o filamento.
   *
   * A mesma amplitude que desfaz a linha principal em pó é exatamente a que
   * faz um ramo: pedaços curtos, desalinhados, que saltam para fora e voltam.
   */
  const scale = branch ? noise.scale * 2.2 : noise.scale;
  const seconds = branch ? noise.seconds * 0.8 : noise.seconds;
  const travel = 700;
  const dur = `${seconds}s`;
  return (
    <filter
      id={jewelFilterId(family, kind)}
      colorInterpolationFilters="sRGB"
      /*
       * A região é generosa de propósito.
       *
       * O que o filtro produz fora dela é CORTADO A DIREITO, e era essa a
       * origem dos blocos e das linhas retas à volta do efeito: o ruído
       * empurrava o brilho para lá da caixa e o browser aparava-o com um
       * retângulo. Nada aqui desenha um retângulo; o retângulo era o limite.
       *
       * Grande demais é o mesmo defeito pelo outro lado: a 340% o buffer do
       * filtro por elemento fica enorme, o compositor corta-o na mesma, e
       * voltou a aparecer um bloco - desta vez no canto superior esquerdo.
       * Cinquenta por cento de folga de cada lado chegam de sobra para os cinco
       * pixels que o ruído desloca, e cabem no que o browser aloca sem cortar.
       */
      x="-120%"
      y="-120%"
      width="340%"
      height="340%"
    >
      <feTurbulence
        type="turbulence"
        baseFrequency={frequency}
        numOctaves={octaves}
        seed={3}
        result="noise-down"
      />
      <feOffset in="noise-down" dx="0" dy="0" result="flow-down">
        <animate
          attributeName="dy"
          values={`${travel}; 0`}
          dur={dur}
          repeatCount="indefinite"
          calcMode="linear"
        />
      </feOffset>

      <feTurbulence
        type="turbulence"
        baseFrequency={frequency}
        numOctaves={octaves}
        seed={3}
        result="noise-up"
      />
      <feOffset in="noise-up" dx="0" dy="0" result="flow-up">
        <animate
          attributeName="dy"
          values={`0; -${travel}`}
          dur={dur}
          repeatCount="indefinite"
          calcMode="linear"
        />
      </feOffset>

      <feTurbulence
        type="turbulence"
        baseFrequency={frequency}
        numOctaves={octaves}
        seed={7}
        result="noise-right"
      />
      <feOffset in="noise-right" dx="0" dy="0" result="flow-right">
        <animate
          attributeName="dx"
          values={`${travel}; 0`}
          dur={dur}
          repeatCount="indefinite"
          calcMode="linear"
        />
      </feOffset>

      <feTurbulence
        type="turbulence"
        baseFrequency={frequency}
        numOctaves={octaves}
        seed={7}
        result="noise-left"
      />
      <feOffset in="noise-left" dx="0" dy="0" result="flow-left">
        <animate
          attributeName="dx"
          values={`0; -${travel}`}
          dur={dur}
          repeatCount="indefinite"
          calcMode="linear"
        />
      </feOffset>

      <feBlend in="flow-down" in2="flow-up" mode="color-dodge" result="vertical" />
      <feBlend in="flow-right" in2="flow-left" mode="color-dodge" result="horizontal" />
      <feBlend in="vertical" in2="horizontal" mode="color-dodge" result="turbulent" />
      <feDisplacementMap
        in="SourceGraphic"
        in2="turbulent"
        scale={scale}
        xChannelSelector="R"
        yChannelSelector="B"
      />
    </filter>
  );
}

/**
 * O SVG partilhado. Montado uma vez, pela secção das joias.
 *
 * Não desenha nada: é só um armazém de filtros. Fica fora do fluxo e é invisível
 * para quem lê o ecrã.
 */
export function JewelElectricFilters() {
  const root = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    const svg = root.current;
    if (!svg) {
      return;
    }
    return registerJewelFilters(svg);
  }, []);

  return (
    <svg
      ref={root}
      className="jewel-filter-defs"
      aria-hidden="true"
      focusable="false"
      width="0"
      height="0"
    >
      <title>Filtros elétricos das joias</title>
      <defs>
        {JEWEL_FAMILIES.map((family) => (
          <ElectricFilter key={family} family={family} kind="arc" />
        ))}
        {JEWEL_FAMILIES.map((family) => (
          <ElectricFilter key={`${family}-branch`} family={family} kind="branch" />
        ))}
      </defs>
    </svg>
  );
}

/** Só para as provas: as famílias que têm mesmo um filtro declarado. */
export const JEWEL_FILTERED_FAMILIES = Object.keys(JEWEL_FAMILY_BEHAVIOURS);
